import { useCallback, useMemo } from 'react'

import { Checkbox, Select, Tag } from 'antd'
import { isArray, isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import { isNonEmptyArray, isObject } from '../../../utils'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import '../../../assets/entities-selector.scss'

const EntitiesSelector = ({
  checkable = false, className = '', disabled, dropdownMatchSelectWidth, dropdownStyle = {}, empty = false, entityOptions, highlightValues = [], loading,
  maxTagCount, maxTagPlaceholder, multiple = 'multiple', notFoundContent, optionLabelProp, placeholder, showSearch = false, style = {}, tagPlaceholder = true, values = [],
  onChange, onSearch, dropdownRender, renderOption, selectorClassName = ''
}) => {
  const t = useSelector(selectTranslate)

  const highlightMap = useMemo(() => {
    const map = {}

    if (isNonEmptyArray(highlightValues)) {
      highlightValues.forEach(value => {
        map[value] = true
      })
    }

    return map
  }, [highlightValues])

  const sortedOptions = useMemo(() => {
    let sortedOptions = entityOptions

    if (Object.keys(highlightMap).length > 0) {
      sortedOptions = [...sortedOptions].sort((a, b) => {
        const aWeight = highlightMap[a.value] ? 1 : 0
        const bWeight = highlightMap[b.value] ? 1 : 0

        return bWeight - aWeight
      })
    }

    return sortedOptions
  }, [highlightMap, entityOptions])

  const handleFilter = useCallback((input, option) => {
    if (isFunction(onSearch)) {
      return true
    } else {
      const { label, title } = option.props

      return (
        (label && label.toLowerCase().includes(input.toLowerCase())) ||
        (title && title.toLowerCase().includes(input.toLowerCase()))
      )
    }
  }, [onSearch])

  const handleOptionChange = useCallback((value, option) => {
    let entities = null

    if (isArray(option)) {
      entities = option.map(o => o.props.entity)
    } else if (isObject(option?.props)) {
      entities = option.props.entity
    }

    onChange(value, option, entities)
  }, [onChange])

  const selectedValues = useMemo(() => {
    if (isArray(values)) {
      return values
    }

    return values === null || values === undefined ? [] : [values]
  }, [values])

  const renderEntityOption = useCallback(entityOption => {
    const content = isFunction(renderOption) ? renderOption(entityOption.entity) : entityOption.label

    return (
      <Select.Option
        key={'entity_' + entityOption.value}
        className={className + (highlightMap[entityOption.value] ? ' color-neutral' : '')}
        title={entityOption.tooltip}
        value={entityOption.value}
        label={entityOption.label}
        disabled={entityOption.disabled}
        entity={entityOption.entity}
      >
        {checkable
          ? (
            <span className='entities-selector-option'>
              {/** The row itself is what antd listens to : the box only pictures the state. */}
              <Checkbox checked={selectedValues.includes(entityOption.value)} />
              <span className='entities-selector-option__label'>{content}</span>
            </span>
          )
          : content}
      </Select.Option>
    )
  }, [checkable, className, highlightMap, renderOption, selectedValues])

  const optionNodes = useMemo(() => {
    if (!sortedOptions.some(entityOption => entityOption.group)) {
      return sortedOptions.map(renderEntityOption)
    }

    const groups = new Map()

    sortedOptions.forEach(entityOption => {
      const group = entityOption.group ?? ''

      if (!groups.has(group)) {
        groups.set(group, { label: entityOption.groupLabel ?? group, options: [] })
      }

      groups.get(group).options.push(entityOption)
    })

    return [...groups.entries()].map(([group, { label, options }]) => group
      ? <Select.OptGroup key={'group_' + group} label={label}>{options.map(renderEntityOption)}</Select.OptGroup>
      : options.map(renderEntityOption)
    )
  }, [sortedOptions, renderEntityOption])

  const summarizeSelection = useCallback(omittedValues => {
    if (omittedValues.length === 1) {
      const option = entityOptions.find(entityOption => String(entityOption.value) === String(omittedValues[0]))

      return option?.label ?? omittedValues[0]
    }

    return t('entities_selector.selected_count', { count: omittedValues.length })
  }, [entityOptions, t])

  const Placeholder = useMemo(() => {
    if (isNonEmptyArray(values) || !placeholder) {
      return false
    }

    return tagPlaceholder ? <Tag className='fs-15 h-20'><span>{placeholder}</span></Tag> : placeholder
  }, [values, placeholder, tagPlaceholder])

  return (
    <Select
      className={selectorClassName}
      placeholder={Placeholder}
      disabled={disabled}
      loading={loading}
      mode={multiple}
      allowClear
      showSearch={showSearch}
      value={values}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={maxTagPlaceholder ?? (maxTagCount === 0 ? summarizeSelection : undefined)}
      optionFilterProp='label'
      optionLabelProp={optionLabelProp ?? (checkable ? 'label' : undefined)}
      notFoundContent={notFoundContent}
      onChange={handleOptionChange}
      style={{ ...style }}
      dropdownClassName={checkable ? 'entities-selector-checkable' : undefined}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      dropdownStyle={{ maxHeight: 400, overflow: 'auto', ...dropdownStyle }}
      onSearch={onSearch}
      filterOption={handleFilter}
      dropdownRender={isFunction(dropdownRender) ? dropdownRender : undefined}
    >
      {empty && (
        <Select.Option
          title='/'
          value={null}
          label='/'
          entity={null}
        >
              /
        </Select.Option>
      )}
      {optionNodes}
    </Select>
  )
}

export default EntitiesSelector
